/**
 * Historical Permits Report Page
 *
 * Displays a filterable table of permits with download capabilities.
 * Supports single and bulk downloads in DOCX and PDF formats.
 *
 * @module UiReportAllPermitsReport
 */
import React from "react";
import { Box, Typography, ThemeProvider } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import MaterialTable from "material-table";

// Shared Components
import Panel from "../../../components/panels/Panel";
import MultiOptionsPicker from "../../../components/pickers/MultiOptionsPicker";
import Loader from "../../../components/Loader";
import InfoBox from "../../../components/InfoBox";

// Local Components
import {
  IssuanceDateDialog,
  TableWrapper,
  Breadcrumbs,
  Divider,
} from "./components";

// Hooks
import {
  usePermitData,
  usePermitDownload,
  usePermitColumns,
  useTableActions,
  useTableTheme,
} from "./hooks";

// Constants
import { TABLE_OPTIONS, exportCsvHandler } from "./constants";

/**
 * Historical Permits Report Component
 *
 * Features:
 * - Year-based filtering of permits
 * - Single permit download (DOCX/PDF)
 * - Bulk permit download with ZIP
 * - Inline edit navigation
 * - Copy to clipboard functionality
 *
 * @returns {JSX.Element} The Historical Permits Report page
 */
const UiReportAllPermitsReport = () => {
  // Data fetching and filtering
  const {
    filteredData,
    isLoading,
    error,
    yearsOptions,
    selectedYears,
    setSelectedYears,
    selectedRows,
    setSelectedRows,
  } = usePermitData();

  // Download management
  const {
    dateDialogOpen,
    selectedDate,
    setSelectedDate,
    openDateDialog,
    closeDateDialog,
    processDownload,
  } = usePermitDownload(selectedRows, yearsOptions);

  // Key to force columns/table recreation when year filter changes
  // This prevents memory leak from MaterialTable's internal tableData accumulation
  const tableKey = selectedYears.join(",");

  // Table configuration - pass tableKey to force column recreation
  const tableTheme = useTableTheme();
  const columns = usePermitColumns(openDateDialog, tableKey);
  const actions = useTableActions({
    filteredData,
    columns,
    selectedCount: selectedRows.length,
    setSelectedRows,
    openDateDialog,
  });

  // Error state
  if (error) {
    return "An error has occurred: " + error.message;
  }

  return (
    <>
      <Helmet title="Permits Report" />
      <Typography variant="h3" gutterBottom display="inline">
        Permits Report
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Permits Report</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <InfoBox title="Permit Document Downloads">
        <Typography variant="body2" color="textSecondary">
          Download official permit documents in DOCX or PDF format. Use the
          action buttons on each row for single downloads, or select multiple
          permits using the checkboxes and use the toolbar buttons for bulk
          downloads. You will be prompted to select an issuance date before
          downloading. Only active permits (with an ID) can be downloaded.
        </Typography>
      </InfoBox>

      <Panel>
        <Box>
          <MultiOptionsPicker
            options={yearsOptions}
            selectedOptions={selectedYears}
            setSelectedOptions={setSelectedYears}
            label="Years Filter"
          />
        </Box>

        <TableWrapper>
          {filteredData ? (
            <ThemeProvider theme={tableTheme}>
              <MaterialTable
                key={tableKey}
                columns={columns}
                data={filteredData}
                isLoading={isLoading}
                options={{ ...TABLE_OPTIONS, exportCsv: exportCsvHandler }}
                actions={actions}
                onSelectionChange={setSelectedRows}
              />
            </ThemeProvider>
          ) : (
            <Loader />
          )}
        </TableWrapper>
      </Panel>

      <IssuanceDateDialog
        open={dateDialogOpen}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onClose={closeDateDialog}
        onConfirm={processDownload}
      />
    </>
  );
};

export default UiReportAllPermitsReport;
